import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TextStyle } from 'editor/src/store/design/types';
import getFontDefinition from 'editor/src/store/fonts/selector/getFontDefinition';
import { useSelector } from 'editor/src/store/hooks';

import useOnClickOutside from 'editor/src/util/useOnClickOutside';

import IconCheck from 'editor/src/component/Icon/IconCheck';
import IconMoreHorizontal from 'editor/src/component/Icon/IconMoreHorizontal';
import {
  formatNumber,
  getFontDisplayName,
  getTextStylePreviewCss,
  isLightColor,
} from 'editor/src/component/TextStyleControl/textStylePreview';

import styles from './index.module.scss';

export type TextStyleCardAction = 'edit' | 'rename' | 'duplicate' | 'delete';

interface Props {
  style: TextStyle;
  usage: number;
  modified: number;
  /** The selected texts follow this style. */
  active: boolean;
  /** Nothing is selected, so clicking adds a text in the style. */
  addsText: boolean;
  /** Authors manage styles; buyers only apply them. */
  canManage: boolean;
  compact: boolean;
  onApply: (style: TextStyle) => void;
  onAction: (style: TextStyle, action: TextStyleCardAction) => void;
  onRename: (style: TextStyle, name: string) => void;
}

/**
 * GAPI-61895: one style in the Text tab: its name rendered in the style's own font, colour and
 * (clamped) size, and "Font · size pt · n texts · m modified". Clicking it applies the style to
 * the selected texts, or adds a text in it when nothing is selected. Authors get a "⋯" menu
 * (Edit style, Rename, Duplicate, Delete); Edit style opens the style editor in place of the list.
 */
function TextStyleCard({
  style,
  usage,
  modified,
  active,
  addsText,
  canManage,
  compact,
  onApply,
  onAction,
  onRename,
}: Props) {
  const { t } = useTranslation();
  const font = useSelector((state) => getFontDefinition(state, style.fontFamily));
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(style.name);
  const menuRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(menuRef, () => setMenuOpen(false), !menuOpen);

  useEffect(() => setName(style.name), [style.name]);

  const usageText = usage === 1 ? t('editor-text-style-usage-one') : t('editor-text-style-usage', { count: usage });
  const sizeText = t('editor-text-style-size-pt', { size: formatNumber(style.fontSize) });
  const metaParts = [getFontDisplayName(style.fontFamily, font), sizeText, usageText];

  const commitRename = () => {
    const next = name.trim();
    setRenaming(false);
    if (next && next !== style.name) {
      onRename(style, next);
    } else {
      setName(style.name);
    }
  };

  const pick = (action: TextStyleCardAction) => {
    setMenuOpen(false);
    if (action === 'rename') {
      setRenaming(true);
    } else {
      onAction(style, action);
    }
  };

  const hint = addsText ? t('editor-text-style-add-in-style') : undefined;
  const metaLine = (
    <span className={cn(styles.meta, 'cy-text-style-meta')}>
      {/* Wraps between the parts, never inside one; a long font name ends in an ellipsis. */}
      {metaParts.map((part, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={index}>
          {index > 0 && ' · '}
          <span className={styles.metaPart}>{part}</span>
        </React.Fragment>
      ))}
      {!!modified && (
        <span className={cn(styles.modified, 'cy-text-style-modified')}>
          {` · ${t('editor-text-style-usage-modified', { count: modified })}`}
        </span>
      )}
    </span>
  );

  return (
    <div
      className={cn(styles.card, 'cy-text-style-card', {
        [styles.active]: active,
        [styles.compact]: compact,
        [styles.readOnly]: !canManage,
        'cy-text-style-card-active': active,
      })}
      data-style-id={style.id}
    >
      {renaming ? (
        <div className={styles.cardBody}>
          <input
            className={cn(styles.renameInput, 'cy-text-style-rename-input')}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            value={name}
            aria-label={t('editor-text-style-name')}
            onChange={(e) => setName(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                commitRename();
              } else if (e.key === 'Escape') {
                setName(style.name);
                setRenaming(false);
              }
            }}
          />
          {metaLine}
        </div>
      ) : (
        <button
          type="button"
          className={cn(styles.cardBody, 'cy-text-style-apply')}
          title={hint}
          aria-label={hint ? `${style.name}: ${hint}` : style.name}
          aria-pressed={active}
          onClick={() => onApply(style)}
        >
          <span
            className={cn(styles.preview, 'cy-text-style-preview', {
              [styles.previewOnDark]: isLightColor(style.color),
            })}
            style={getTextStylePreviewCss(style, compact ? 24 : undefined)}
          >
            {style.name}
          </span>
          {metaLine}
          {addsText && <span className={cn(styles.hint, 'cy-text-style-hint')}>{hint}</span>}
        </button>
      )}
      {active && (
        <span className={styles.check}>
          <IconCheck />
        </span>
      )}
      {canManage && (
        <div className={styles.menuAnchor} ref={menuRef}>
          <button
            type="button"
            className={cn(styles.menuButton, 'cy-text-style-menu')}
            aria-label={t('editor-text-style-options')}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <IconMoreHorizontal />
          </button>
          {menuOpen && (
            <div className={cn(styles.menu, 'cy-text-style-menu-list')} role="menu">
              <button type="button" role="menuitem" className="cy-text-style-edit" onClick={() => pick('edit')}>
                {t('editor-text-style-edit')}
              </button>
              <button type="button" role="menuitem" className="cy-text-style-rename" onClick={() => pick('rename')}>
                {t('editor-rename')}
              </button>
              <button
                type="button"
                role="menuitem"
                className="cy-text-style-duplicate"
                onClick={() => pick('duplicate')}
              >
                {t('editor-duplicate')}
              </button>
              <button
                type="button"
                role="menuitem"
                className={cn(styles.danger, 'cy-text-style-delete')}
                onClick={() => pick('delete')}
              >
                {t('Delete')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default React.memo(TextStyleCard);
